import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useState } from 'react';

export default function KontaktPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('company', formData.company);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d56mhd0f3qcgnii4i8j0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', company: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-[#F8FAFB]">
      {/* Header */}
      <header className="bg-white border-b border-[#E5E7EB] sticky top-0 z-50 backdrop-blur-lg bg-white/95">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3 cursor-pointer">
              <img 
                src="https://public.readdy.ai/ai/img_res/242a1403-cfd7-49be-b11f-231711dbad5d.png" 
                alt="LeadSystem Logo" 
                className="h-8 w-auto object-contain"
              />
              <span className="text-2xl font-bold text-[#0F766E]" style={{ fontFamily: 'Georgia, serif' }}>
                LeadSystem
              </span>
            </Link>
            <Link 
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 text-[#0F766E] hover:bg-[#E0F2F1] rounded-full transition-all font-semibold cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
              Zurück zur Startseite
            </Link>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-6 py-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="text-center mb-16">
            <h1 className="text-5xl md:text-6xl font-bold text-[#2C3E50] mb-6" style={{ fontFamily: 'Georgia, serif' }}>
              Kontaktieren Sie uns
            </h1>
            <p className="text-xl text-[#6B7280] max-w-3xl mx-auto leading-relaxed">
              Haben Sie Fragen zum LeadSystem oder möchten Sie mehr über die Partnerschaft erfahren? Wir sind für Sie da.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div className="bg-white rounded-3xl shadow-lg border border-[#E5E7EB] p-10">
              <h2 className="text-2xl font-bold text-[#2C3E50] mb-6">Nachricht senden</h2>
              
              <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-[#2C3E50] mb-2">
                    Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-[#E5E7EB] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#14B8A6] focus:border-transparent text-base"
                    placeholder="Ihr Name"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-[#2C3E50] mb-2">
                    E-Mail *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-[#E5E7EB] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#14B8A6] focus:border-transparent text-base"
                    placeholder="ihre@email.de"
                  />
                </div>

                <div>
                  <label htmlFor="company" className="block text-sm font-semibold text-[#2C3E50] mb-2">
                    Unternehmen
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-[#E5E7EB] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#14B8A6] focus:border-transparent text-base"
                    placeholder="Ihr Unternehmen"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-semibold text-[#2C3E50] mb-2">
                    Nachricht *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    maxLength={500}
                    rows={5}
                    className="w-full px-4 py-3 border border-[#E5E7EB] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#14B8A6] focus:border-transparent text-base resize-none"
                    placeholder="Ihre Nachricht (max. 500 Zeichen)"
                  />
                  <div className="text-xs text-[#6B7280] mt-2 text-right">
                    {formData.message.length}/500 Zeichen
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full px-8 py-4 bg-[#14B8A6] text-white rounded-full hover:bg-[#0D9488] transition-all hover:shadow-xl font-semibold disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                >
                  {isSubmitting ? 'Wird gesendet...' : 'Nachricht senden'}
                </button>

                {submitStatus === 'success' && (
                  <div className="p-4 bg-[#D1FAE5] border border-[#10B981] rounded-xl">
                    <p className="text-[#065F46] text-sm font-medium">
                      ✓ Vielen Dank! Ihre Nachricht wurde erfolgreich gesendet.
                    </p>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="p-4 bg-[#FEE2E2] border border-[#DC2626] rounded-xl">
                    <p className="text-[#991B1B] text-sm font-medium">
                      ✗ Es gab einen Fehler beim Senden. Bitte versuchen Sie es erneut.
                    </p>
                  </div>
                )}
              </form>
            </div>

            {/* Contact Information */}
            <div className="space-y-8">
              {/* Company Info */}
              <div className="bg-gradient-to-br from-[#E0F2F1] to-[#B2DFDB] rounded-3xl p-10">
                <div className="w-16 h-16 bg-[#0F766E] rounded-2xl flex items-center justify-center mb-6">
                  <i className="ri-building-line text-white text-3xl"></i>
                </div>
                <h3 className="text-2xl font-bold text-[#2C3E50] mb-4">Future Now UG</h3>
                <div className="space-y-3 text-[#374151]">
                  <p className="flex items-start gap-3">
                    <i className="ri-map-pin-line text-[#0F766E] text-xl mt-1"></i>
                    <span>Max-Planck-Str. 4<br />85609 Aschheim<br />Deutschland</span>
                  </p>
                  <p className="flex items-center gap-3">
                    <i className="ri-mail-line text-[#0F766E] text-xl"></i>
                    <a href="mailto:info@future-now.online" className="hover:text-[#0F766E] transition-colors">
                      info@future-now.online
                    </a>
                  </p>
                </div>
              </div>

              {/* Quick Links */}
              <div className="bg-white rounded-3xl shadow-lg border border-[#E5E7EB] p-10">
                <h3 className="text-xl font-bold text-[#2C3E50] mb-6">Schnellzugriff</h3>
                <div className="space-y-4">
                  <Link 
                    to="/admin"
                    className="flex items-center gap-3 text-[#374151] hover:text-[#0F766E] transition-colors group cursor-pointer"
                  >
                    <div className="w-10 h-10 bg-[#E0F2F1] rounded-lg flex items-center justify-center group-hover:bg-[#B2DFDB] transition-colors">
                      <i className="ri-user-add-line text-[#0F766E]"></i>
                    </div>
                    <span className="font-medium">Partner-Zugang anfragen</span>
                  </Link>
                  <Link 
                    to="/crm"
                    className="flex items-center gap-3 text-[#374151] hover:text-[#0F766E] transition-colors group cursor-pointer"
                  >
                    <div className="w-10 h-10 bg-[#E0F2F1] rounded-lg flex items-center justify-center group-hover:bg-[#B2DFDB] transition-colors">
                      <i className="ri-dashboard-line text-[#0F766E]"></i>
                    </div>
                    <span className="font-medium">CRM-Demo ansehen</span>
                  </Link>
                  <a 
                    href="/#funktionsweise"
                    className="flex items-center gap-3 text-[#374151] hover:text-[#0F766E] transition-colors group cursor-pointer"
                  >
                    <div className="w-10 h-10 bg-[#E0F2F1] rounded-lg flex items-center justify-center group-hover:bg-[#B2DFDB] transition-colors">
                      <i className="ri-information-line text-[#0F766E]"></i>
                    </div>
                    <span className="font-medium">Funktionsweise verstehen</span>
                  </a>
                </div>
              </div>

              {/* Social Media */}
              <div className="bg-white rounded-3xl shadow-lg border border-[#E5E7EB] p-10">
                <h3 className="text-xl font-bold text-[#2C3E50] mb-6">Folgen Sie uns</h3>
                <div className="flex gap-4">
                  <a 
                    href="https://linkedin.com" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-12 h-12 border-2 border-[#E5E7EB] rounded-xl flex items-center justify-center hover:border-[#0F766E] hover:bg-[#E0F2F1] transition-all cursor-pointer"
                  >
                    <i className="ri-linkedin-line text-[#0F766E] text-xl"></i>
                  </a>
                  <a 
                    href="https://xing.com" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-12 h-12 border-2 border-[#E5E7EB] rounded-xl flex items-center justify-center hover:border-[#0F766E] hover:bg-[#E0F2F1] transition-all cursor-pointer"
                  >
                    <i className="ri-xing-line text-[#0F766E] text-xl"></i>
                  </a>
                  <a 
                    href="mailto:info@future-now.online"
                    className="w-12 h-12 border-2 border-[#E5E7EB] rounded-xl flex items-center justify-center hover:border-[#0F766E] hover:bg-[#E0F2F1] transition-all cursor-pointer"
                  >
                    <i className="ri-mail-line text-[#0F766E] text-xl"></i>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
